import * as THREE from "three";
import { editorStore } from "../../store/editorStore";
import type { SymbolInsertGeometry } from "../../utils/symbolAttachmentGeometry";
// Helper function to interpolate points on a quadratic Bezier curve
function getQuadraticBezierPoint(t: number, p0: THREE.Vector2, p1: THREE.Vector2, p2: THREE.Vector2): THREE.Vector2 {
  const invT = 1 - t;
  const x = invT * invT * p0.x + 2 * invT * t * p1.x + t * t * p2.x;
  const y = invT * invT * p0.y + 2 * invT * t * p1.y + t * t * p2.y;
  return new THREE.Vector2(x, y);
}

/**
 * Calculates the vertices for wall line segments based on shape type and parameters.
 *
 * For straight walls (shapeType === "straight"), this function always returns exactly 4 points:
 * [topLeft, topRight, bottomRight, bottomLeft] in clockwise order.
 * This is critical for the edge-based connection system where walls must be 4-point rectangles.
 *
 * For arc walls, this function returns variable points based on the segments count (segmentsCount * 2).
 * Arc walls are handled separately and excluded from corner state management.
 *
 * For endcap variants, this function returns 4, 8, or more points but are handled separately.
 */
export const calculateLineVertices = (
  start: THREE.Vector2,
  end: THREE.Vector2,
  offset: number,
  lineWeight: number,
  wallWidth: number,
  shapeType: "straight" | "left" | "right" | "arc" | "arcLeft" | "arcRight" | "endCapBoth" | "endCapStart" | "endCapEnd" | "partialEndCapStart" | "partialEndCapEnd",
  controlPoint?: THREE.Vector2,
  connectedWallWidth?: number,
  arcSymbolInserts?: SymbolInsertGeometry[],
): THREE.Vector2[] => {
  const direction = new THREE.Vector2().subVectors(end, start);

  // Check for zero-length vector to prevent NaN values from normalize()
  const length = direction.length();
  if (length < 0.00001) {
    // Return empty array for degenerate (zero-length) walls
    return [];
  }

  direction.normalize();
  const width = shapeType === "straight" || shapeType === "arc" ? wallWidth : lineWeight;
  const perpendicular = new THREE.Vector2(
    -direction.y,
    direction.x,
  ).multiplyScalar(width / 2);
  const offsetVector = perpendicular.clone().multiplyScalar(offset / width);
  const extendLength = 0;

  start = start.clone().sub(direction.clone().multiplyScalar(extendLength));
  end = end.clone().add(direction.clone().multiplyScalar(extendLength));
  const startOffset = start.clone().add(offsetVector);
  const endOffset = end.clone().add(offsetVector);

  // Temporary Recalculate control point based on the midpoint and perpendicular direction
  if (!controlPoint) {
    const tempDirection = new THREE.Vector2().subVectors(start, end);
    const tempLength = tempDirection.length();
    if (tempLength >= 0.00001) {
      controlPoint = new THREE.Vector2()
        .addVectors(start, end)
        .multiplyScalar(0.5)
        .add(tempDirection.normalize().rotateAround(new THREE.Vector2(0, 0), Math.PI / 2).multiplyScalar(-2));
    } else {
      // Use a default control point if start and end are too close
      controlPoint = new THREE.Vector2()
        .addVectors(start, end)
        .multiplyScalar(0.5)
        .add(new THREE.Vector2(0, -2));
    }
  }

  if (
    shapeType === "endCapBoth" ||
    shapeType === "endCapStart" ||
    shapeType === "endCapEnd" ||
    shapeType === "partialEndCapStart" ||
    shapeType === "partialEndCapEnd"
  ) {
    // Define the vertices for the end caps as a rectangle with 4 vertices
    let startCap1, startCap2, startCap3, startCap4, endCap1, endCap2, endCap3, endCap4;
    // Extend vertically at the start end cap
    // Use wallWidth directly, similar to how lineWeight is used
    const extendedPerpendicular = perpendicular
      .clone()
      .multiplyScalar((wallWidth - lineWeight) / lineWeight);

    startCap1 = startOffset.clone().add(extendedPerpendicular);
    startCap2 = startOffset.clone().sub(extendedPerpendicular);
    endCap1 = startOffset
      .clone()
      .add(extendedPerpendicular)
    //.sub(direction.clone().multiplyScalar(lineWeight));
    endCap2 = startOffset
      .clone()
      .sub(extendedPerpendicular)
    //.sub(direction.clone().multiplyScalar(lineWeight));

    // Extend vertically at the end end cap
    endCap3 = endOffset.clone().add(extendedPerpendicular);
    endCap4 = endOffset.clone().sub(extendedPerpendicular);
    startCap3 = endOffset
      .clone()
      .add(extendedPerpendicular)
    //.add(direction.clone().multiplyScalar(lineWeight));
    startCap4 = endOffset
      .clone()
      .sub(extendedPerpendicular)
    //.add(direction.clone().multiplyScalar(lineWeight));
    if (shapeType === "endCapBoth") {
      return [
        startCap1,
        startCap2,
        endCap2,
        endCap1, // Start end cap
        endCap3,
        endCap4,
        startCap4,
        startCap3, // End end cap
      ];
    } else if (shapeType === "endCapStart") {
      return [
        startCap1,
        startCap2,
        endCap2,
        endCap1, // Start end cap
      ];
    } else if (shapeType === "endCapEnd") {
      return [
        endCap3,
        endCap4,
        startCap4,
        startCap3, // End end cap
      ];
    } else if (shapeType === "partialEndCapStart" || shapeType === "partialEndCapEnd") {
      // For partial endcaps, we need to create endcaps that only cover the width difference
      const connectedWidth = connectedWallWidth || wallWidth;
      const widthDifference = wallWidth - connectedWidth;

      if (widthDifference <= 0) {
        // No partial endcap needed if current wall is not wider
        return [];
      }

      // Calculate the partial endcap - create two rectangles on each side of the connected wall
      const connectedWallHalfWidth = connectedWidth / 2;

      // Create perpendicular vectors to the OUTER outlines (not lineWeight-based)
      // direction should already be validated above, but double-check
      const basePerpendicularUnit = new THREE.Vector2(-direction.y, direction.x);
      if (basePerpendicularUnit.length() < 0.00001) {
        // If perpendicular is also degenerate, return empty
        return [];
      }
      basePerpendicularUnit.normalize();
      const currentWallPerpendicular = basePerpendicularUnit.clone().multiplyScalar(wallWidth / 2);
      const connectedWallPerpendicular = basePerpendicularUnit.clone().multiplyScalar(connectedWallHalfWidth);

      let offset: THREE.Vector2;
      if (shapeType === "partialEndCapStart") {
        offset = startOffset;
      } else {
        offset = endOffset;
      }

      // We want TWO short outline segments that bridge the gap between the
      // wider and the narrower wall on BOTH sides of the wall centerline.
      // The endcap pipeline later turns >4 vertices into two line shapes by
      // using indices [0,1] and [4,5]. So we construct the vertices so that:
      // - [0]..[1] is the +side bridge (outer current -> outer connected)
      // - [4]..[5] is the -side bridge (outer current -> outer connected)
      const plusSideCurrent = offset.clone().add(currentWallPerpendicular);
      const plusSideConnected = offset.clone().add(connectedWallPerpendicular);
      const minusSideCurrent = offset.clone().sub(currentWallPerpendicular);
      const minusSideConnected = offset.clone().sub(connectedWallPerpendicular);

      // Populate with some filler in between to satisfy the >4 length check
      const result = [
        // +side bridge
        plusSideCurrent,
        plusSideConnected,
        // fillers (not used by createShapes for endcaps)
        plusSideConnected.clone(),
        plusSideCurrent.clone(),
        // -side bridge
        minusSideCurrent,
        minusSideConnected,
        // fillers
        minusSideConnected.clone(),
        minusSideCurrent.clone(),
      ];

      return result;
    }
  } else if ((shapeType === "arc" || shapeType === "arcLeft" || shapeType === "arcRight") && controlPoint) {
    const segmentsCount = 20;  // Increase segments for smoothness

    const outerPoints: THREE.Vector2[] = [];
    const innerPoints: THREE.Vector2[] = [];
    // Chord-based perpendicular is kept as a safe fallback if both tangents degenerate.
    const perpendicularToStartEnd = new THREE.Vector2(-direction.y, direction.x);
    const halfWallWidth = wallWidth / 2;

    // Precompute the Bezier tangent (and perpendicular) at the curve endpoints.
    // These must stay consistent with the snapping logic in snapToOtherObjects.ts
    // (buildWallEndpointCorners / computeWallEdgePointsForSnap) so that the wall's
    // visual corners match the corners used for snapping.
    //
    // Quadratic Bezier derivative:
    //   B'(0) = 2 * (controlPoint - start)  -> tangent at start
    //   B'(1) = 2 * (end - controlPoint)    -> tangent at end
    const startTangent = new THREE.Vector2(controlPoint.x - start.x, controlPoint.y - start.y);
    const endTangent = new THREE.Vector2(end.x - controlPoint.x, end.y - controlPoint.y);
    const startPerp = startTangent.lengthSq() > 1e-10
      ? new THREE.Vector2(-startTangent.y, startTangent.x).normalize()
      : perpendicularToStartEnd.clone();
    const endPerp = endTangent.lengthSq() > 1e-10
      ? new THREE.Vector2(-endTangent.y, endTangent.x).normalize()
      : perpendicularToStartEnd.clone();

    // Helper: compute the perpendicular at a Bézier parameter t using the tangent
    const getPerpAtT = (t: number): THREE.Vector2 => {
      if (t <= 0) return startPerp.clone();
      if (t >= 1) return endPerp.clone();
      const tangent = new THREE.Vector2().subVectors(
        getQuadraticBezierPoint(t + 0.01, start, controlPoint!, end),
        getQuadraticBezierPoint(t, start, controlPoint!, end),
      );
      const tangentLength = tangent.length();
      return tangentLength >= 0.00001
        ? new THREE.Vector2(-tangent.y / tangentLength, tangent.x / tangentLength)
        : perpendicularToStartEnd.clone();
    };

    // Helper: push outer+inner pair for a point+perpendicular
    const addPoints = (pt: THREE.Vector2, perp: THREE.Vector2) => {
      if (shapeType === "arc") {
        outerPoints.push(pt.clone().addScaledVector(perp, halfWallWidth));
        innerPoints.push(pt.clone().addScaledVector(perp, -halfWallWidth));
      } else if (shapeType === "arcLeft") {
        outerPoints.push(pt.clone().addScaledVector(perp, halfWallWidth + lineWeight));
        innerPoints.push(pt.clone().addScaledVector(perp, halfWallWidth));
      } else { // arcRight
        outerPoints.push(pt.clone().addScaledVector(perp, -halfWallWidth));
        innerPoints.push(pt.clone().addScaledVector(perp, -(halfWallWidth + lineWeight)));
      }
    };

    // Helper: emit arc samples for t-range [t0, t1] using arc tangent perpendiculars.
    // includeEnd: whether to emit the sample at t1 (false when the next element will emit it).
    const emitArcRange = (t0: number, t1: number, includeEnd: boolean) => {
      if (t1 - t0 < 1e-5) {
        if (includeEnd) addPoints(getQuadraticBezierPoint(t1, start, controlPoint!, end), getPerpAtT(t1));
        return;
      }
      const sub = Math.max(2, Math.round(segmentsCount * (t1 - t0)));
      const count = includeEnd ? sub : sub - 1;
      for (let i = 0; i <= count; i++) {
        const t = t0 + (t1 - t0) * (i / sub);
        const pt = getQuadraticBezierPoint(t, start, controlPoint!, end);
        addPoints(pt, getPerpAtT(t));
      }
    };

    // --- Piecewise arc + straight-insert path ---
    if (arcSymbolInserts && arcSymbolInserts.length > 0) {
      // Sort inserts by tA and process piecewise
      const sorted = [...arcSymbolInserts].sort((a, b) => a.tA - b.tA);
      let currentT = 0;

      for (const ins of sorted) {
        const { tA, tB, pA, pB } = ins;

        // Insert chord perpendicular (constant across the straight section)
        const chord = pB.clone().sub(pA);
        const chordLen = chord.length();
        const insertPerp = chordLen > 1e-6
          ? new THREE.Vector2(-chord.y / chordLen, chord.x / chordLen)
          : getPerpAtT((tA + tB) / 2);

        // Arc sub-segment leading up to this insert (does NOT emit the tA point)
        emitArcRange(currentT, tA, false);

        // Transition: pA with insert perpendicular, then pB with insert perpendicular
        addPoints(pA, insertPerp);
        addPoints(pB, insertPerp);

        currentT = tB;
      }

      // Final arc sub-segment from last insert to end (includes t=1)
      emitArcRange(currentT, 1, true);

    } else {
      // --- Standard single-arc path (no inserts) ---
      for (let i = 0; i <= segmentsCount; i++) {
        const t = i / segmentsCount;
        const pointOnCurve = getQuadraticBezierPoint(t, start, controlPoint!, end);
        let perpendicular;
        if (i === 0) {
          perpendicular = startPerp.clone();
        } else if (i === segmentsCount) {
          perpendicular = endPerp.clone();
        } else {
          perpendicular = getPerpAtT(t);
        }
        addPoints(pointOnCurve, perpendicular);
      }
    }

    // Reverse the inner points to ensure proper winding of vertices
    innerPoints.reverse();

    // Combine outer and inner points to form the vertices of the line
    const allPoints = [...outerPoints, ...innerPoints];
    return allPoints;
  } else if (shapeType === "left") {
    return [
      startOffset.clone().add(perpendicular),
      endOffset.clone().add(perpendicular),
    ];
  } else if (shapeType === "right") {
    return [
      startOffset.clone().sub(perpendicular),
      endOffset.clone().sub(perpendicular),
    ];
  } else {
    const result = [
      startOffset.clone().add(perpendicular),
      startOffset.clone().sub(perpendicular),
      endOffset.clone().sub(perpendicular),
      endOffset.clone().add(perpendicular),
    ];

    // Validate that straight walls always return exactly 4 points
    if (shapeType === "straight") {
      if (result.length !== 4) {
        console.error("calculateLineVertices: straight walls must return exactly 4 points, got:", result.length);
        if (process.env.NODE_ENV === "development") {
          throw new Error(`calculateLineVertices: expected 4 points for straight wall, got ${result.length}`);
        }
      }
    }

    return result;
  }

  // Fallback return (should never be reached)
  return [];
};
